"use client";

import { useCallback, useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { fetchWithCsrf } from "./fetchWithCsrf";
import { resolveApiUrl } from "./resolveApiUrl";

type DevAdminUser = {
  id: string;
  email: string;
  name: string | null;
  role: "user" | "operator";
  emailVerified: boolean;
  image: string | null;
  createdAt: string;
};

type ListUsersResponse = {
  users: DevAdminUser[];
};

type ImpersonateResponse = {
  ok: true;
  user: {
    id: string;
    email: string;
    name: string | null;
    role: "user" | "operator";
  };
};

type Props = {
  onUnavailable: () => void;
};

export const DevAdminPageClient = ({ onUnavailable }: Props) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<DevAdminUser[] | null>(null);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [signingInEmail, setSigningInEmail] = useState<string | null>(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(resolveApiUrl("/api/dev-admin/users"), {
        credentials: "include"
      });
      if (response.status === 404) {
        onUnavailable();
        return;
      }
      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? t("devAdmin.loadFailed"));
      }
      const data = (await response.json()) as ListUsersResponse;
      setUsers(data.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("devAdmin.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [onUnavailable, t]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  const signInAs = async (targetEmail: string) => {
    const normalized = targetEmail.trim().toLowerCase();
    if (!normalized) {
      setError(t("devAdmin.emailRequired"));
      return;
    }
    setSigningInEmail(normalized);
    setError(null);
    try {
      const response = await fetchWithCsrf("/api/dev-admin/impersonate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalized })
      });
      if (response.status === 404) {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        if (data?.error === "User not found") {
          throw new Error(t("devAdmin.userNotFound"));
        }
        onUnavailable();
        return;
      }
      if (!response.ok) {
        const data = (await response.json().catch(() => null)) as { error?: string } | null;
        throw new Error(data?.error ?? t("devAdmin.signInFailed"));
      }
      await (response.json() as Promise<ImpersonateResponse>);
      window.location.assign("/dashboard");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("devAdmin.signInFailed"));
    } finally {
      setSigningInEmail(null);
    }
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void signInAs(email);
  };

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400">
        {t("devAdmin.envBadge")}
      </div>
      <h1 className="mb-2 text-2xl font-semibold">{t("devAdmin.title")}</h1>
      <p className="mb-8 text-sm text-muted-foreground">{t("devAdmin.intro")}</p>

      <form className="mb-8 flex flex-wrap items-end gap-3" onSubmit={handleSubmit}>
        <div className="min-w-[16rem] flex-1 space-y-1.5">
          <Label htmlFor="dev-admin-email">{t("devAdmin.emailLabel")}</Label>
          <Input
            id="dev-admin-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("devAdmin.emailPlaceholder")}
            disabled={signingInEmail !== null}
          />
        </div>
        <Button type="submit" disabled={signingInEmail !== null}>
          {signingInEmail ? t("devAdmin.signingIn") : t("devAdmin.signInAs")}
        </Button>
      </form>

      {error ? <p className="mb-4 text-sm text-destructive">{error}</p> : null}

      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-medium">{t("devAdmin.usersTitle")}</h2>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={loading || signingInEmail !== null}
          onClick={() => void loadUsers()}
        >
          {t("devAdmin.refresh")}
        </Button>
      </div>

      {loading && !users ? (
        <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
      ) : users && users.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("devAdmin.noUsers")}</p>
      ) : users ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("devAdmin.tableEmail")}</TableHead>
              <TableHead>{t("devAdmin.tableName")}</TableHead>
              <TableHead>{t("devAdmin.tableRole")}</TableHead>
              <TableHead className="text-right">{t("devAdmin.tableActions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell className="font-medium">{user.email}</TableCell>
                <TableCell>{user.name ?? "—"}</TableCell>
                <TableCell>{user.role}</TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={signingInEmail !== null}
                    onClick={() => {
                      setEmail(user.email);
                      void signInAs(user.email);
                    }}
                  >
                    {signingInEmail === user.email.toLowerCase()
                      ? t("devAdmin.signingIn")
                      : t("devAdmin.signInAs")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </div>
  );
};
